/**
 * TS Dependency Graph Visualizer — force-directed graph, same engine as
 * SchemaGraphPage/ConceptGraphVisualizer: Web Worker layout, canvas render
 * with viewport culling, spatial hit-grid, pan/zoom, label-direction placement.
 *
 * Full graph by default: every file node AND every export-symbol node is drawn,
 * with both import (file→file) and uses (file→export) edges. Use the category
 * filters (e.g. hide "Export") to collapse to a file-level architecture view.
 */
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { TsGraphData, TsNode, TsEdge, getCategoryColor as getColor } from './types';

interface SimNode {
  id: string;
  x: number; y: number; vx: number; vy: number;
  name: string;
  nameLen: number;
  category: string;
  color: string;
  radius: number;
  dependents: number;
  labelDir?: 'top' | 'bottom' | 'left' | 'right';
}

interface SimEdge {
  source: string;
  target: string;
  type?: string;
}

// ── Web Worker for force simulation (repulsion + collision + attraction + center) ──

const WORKER_CODE = `
self.onmessage = function(evt) {
  var data = evt.data;
  var nodes = data.nodes;
  var edges = data.edges;
  var width = data.width;
  var height = data.height;
  var iterations = data.iterations || 200;

  var nodeMap = {};
  for (var i = 0; i < nodes.length; i++) nodeMap[nodes[i].id] = nodes[i];

  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    var labelHalfW = Math.min(n.nameLen || 6, 20) * 4;
    n.cr = Math.max(n.radius + 10, labelHalfW);
  }

  var cx = width / 2;
  var cy = height / 2;
  var initRadius = Math.min(width, height) * 0.42;
  for (var i = 0; i < nodes.length; i++) {
    var angle = (2 * Math.PI * i) / nodes.length;
    nodes[i].x = cx + initRadius * Math.cos(angle) + (Math.random() - 0.5) * 40;
    nodes[i].y = cy + initRadius * Math.sin(angle) + (Math.random() - 0.5) * 40;
    nodes[i].vx = 0;
    nodes[i].vy = 0;
  }

  var alpha0 = 1.0;
  var alphaDecay = 1 - Math.pow(0.001, 1 / iterations);
  var cellSize = 160;

  for (var iter = 0; iter < iterations; iter++) {
    var alpha = alpha0 * Math.pow(1 - alphaDecay, iter);
    if (alpha < 0.001) break;

    var grid = {};
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var gx = Math.floor(n.x / cellSize);
      var gy = Math.floor(n.y / cellSize);
      var key = gx + ',' + gy;
      if (!grid[key]) grid[key] = [];
      grid[key].push(i);
    }

    // Repulsion
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      var gx = Math.floor(a.x / cellSize);
      var gy = Math.floor(a.y / cellSize);
      for (var dx = -1; dx <= 1; dx++) {
        for (var dy = -1; dy <= 1; dy++) {
          var key = (gx + dx) + ',' + (gy + dy);
          var cell = grid[key];
          if (!cell) continue;
          for (var ci = 0; ci < cell.length; ci++) {
            var j = cell[ci];
            if (j <= i) continue;
            var b = nodes[j];
            var ddx = b.x - a.x;
            var ddy = b.y - a.y;
            var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
            var strength = -900 * alpha / (dist * dist);
            var fx = ddx / dist * strength;
            var fy = ddy / dist * strength;
            a.vx -= fx; a.vy -= fy;
            b.vx += fx; b.vy += fy;
          }
        }
      }
    }

    // Collision (label-aware radius, prevents overlap)
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      var gx = Math.floor(a.x / cellSize);
      var gy = Math.floor(a.y / cellSize);
      for (var dx = -1; dx <= 1; dx++) {
        for (var dy = -1; dy <= 1; dy++) {
          var key = (gx + dx) + ',' + (gy + dy);
          var cell = grid[key];
          if (!cell) continue;
          for (var ci = 0; ci < cell.length; ci++) {
            var j = cell[ci];
            if (j <= i) continue;
            var b = nodes[j];
            var ddx = b.x - a.x;
            var ddy = b.y - a.y;
            var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
            var minDist = (a.cr + b.cr) * 1.15;
            if (dist < minDist) {
              var overlap = (minDist - dist) * 0.5;
              var ox = (ddx / dist) * overlap;
              var oy = (ddy / dist) * overlap;
              a.vx -= ox; a.vy -= oy;
              b.vx += ox; b.vy += oy;
            }
          }
        }
      }
    }

    // Attraction (edges)
    for (var ei = 0; ei < edges.length; ei++) {
      var edge = edges[ei];
      var a = nodeMap[edge.source];
      var b = nodeMap[edge.target];
      if (!a || !b) continue;
      var ddx = b.x - a.x;
      var ddy = b.y - a.y;
      var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
      var targetDist = 140;
      var strength = (dist - targetDist) * 0.03 * alpha;
      var fx = ddx / dist * strength;
      var fy = ddy / dist * strength;
      a.vx += fx; a.vy += fy;
      b.vx -= fx; b.vy -= fy;
    }

    // Center force
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].vx += (cx - nodes[i].x) * 0.008 * alpha;
      nodes[i].vy += (cy - nodes[i].y) * 0.008 * alpha;
    }

    var maxV = 0;
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].vx *= 0.6;
      nodes[i].vy *= 0.6;
      nodes[i].x += nodes[i].vx;
      nodes[i].y += nodes[i].vy;
      var v = Math.abs(nodes[i].vx) + Math.abs(nodes[i].vy);
      if (v > maxV) maxV = v;
    }
    if (maxV < 0.5) break;
  }

  var positions = [];
  for (var i = 0; i < nodes.length; i++) {
    positions.push({ id: nodes[i].id, x: nodes[i].x, y: nodes[i].y });
  }
  self.postMessage({ positions: positions });
};
`;

let workerBlobUrl: string | null = null;
function getWorkerUrl(): string {
  if (!workerBlobUrl) {
    workerBlobUrl = URL.createObjectURL(new Blob([WORKER_CODE], { type: 'application/javascript' }));
  }
  return workerBlobUrl;
}

// ── Canvas renderer ──────────────────────────────────────────────────────

function drawGraph(
  ctx: CanvasRenderingContext2D,
  nodes: SimNode[],
  edges: SimEdge[],
  nodeMap: Map<string, SimNode>,
  transform: { x: number; y: number; k: number },
  hoveredNode: SimNode | null,
  selectedNode: SimNode | null,
  width: number,
  height: number,
  impactMap: Map<string, number> | null,
) {
  ctx.clearRect(0, 0, width, height);
  ctx.save();
  ctx.translate(transform.x, transform.y);
  ctx.scale(transform.k, transform.k);

  const invK = 1 / transform.k;
  const vLeft = -transform.x * invK - 80;
  const vTop = -transform.y * invK - 80;
  const vRight = (width - transform.x) * invK + 80;
  const vBottom = (height - transform.y) * invK + 80;

  const impactActive = !!impactMap && impactMap.size > 1;

  const connectedIds = new Set<string>();
  if (selectedNode && !impactActive) {
    for (const e of edges) {
      if (e.source === selectedNode.id) connectedIds.add(e.target);
      if (e.target === selectedNode.id) connectedIds.add(e.source);
    }
  }

  const dimEdges = impactActive || !!selectedNode;
  ctx.beginPath();
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.2;
  ctx.globalAlpha = dimEdges ? 0.08 : 0.55;
  const highlightedEdges: SimEdge[] = [];
  const callsEdges: SimEdge[] = [];
  const impactEdges: SimEdge[] = [];
  for (const edge of edges) {
    const src = nodeMap.get(edge.source);
    const tgt = nodeMap.get(edge.target);
    if (!src || !tgt) continue;
    if ((src.x < vLeft && tgt.x < vLeft) || (src.x > vRight && tgt.x > vRight) ||
        (src.y < vTop && tgt.y < vTop) || (src.y > vBottom && tgt.y > vBottom)) continue;
    if (impactActive && impactMap!.has(edge.source) && impactMap!.has(edge.target)) {
      impactEdges.push(edge);
      continue;
    }
    if (!impactActive && selectedNode && (edge.source === selectedNode.id || edge.target === selectedNode.id)) {
      highlightedEdges.push(edge);
      continue;
    }
    if (edge.type === 'calls') {
      callsEdges.push(edge);
      continue;
    }
    ctx.moveTo(src.x, src.y);
    ctx.lineTo(tgt.x, tgt.y);
  }
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Cross-stack HTTP calls edges — dashed purple so the frontend→backend
  // boundary hops stand out from ordinary imports.
  if (callsEdges.length > 0) {
    ctx.beginPath();
    ctx.strokeStyle = '#7c3aed';
    ctx.lineWidth = 1.8;
    ctx.globalAlpha = dimEdges ? 0.1 : 0.7;
    ctx.setLineDash([7, 5]);
    for (const edge of callsEdges) {
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  if (highlightedEdges.length > 0) {
    ctx.beginPath();
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2.5;
    for (const edge of highlightedEdges) {
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
  }

  // Impact ripple edges — amber, on top of everything else.
  if (impactEdges.length > 0) {
    ctx.beginPath();
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.85;
    for (const edge of impactEdges) {
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  const showLabels = transform.k > 0.35;
  for (const node of nodes) {
    if (node.x < vLeft || node.x > vRight || node.y < vTop || node.y > vBottom) continue;

    const isSelected = selectedNode?.id === node.id;
    const isHovered = hoveredNode?.id === node.id;
    const isConnected = selectedNode && connectedIds.has(node.id);
    const inImpact = impactActive && impactMap!.has(node.id);
    const isImpactSource = impactActive && impactMap!.get(node.id) === 0;

    if (impactActive) {
      if (!inImpact) ctx.globalAlpha = 0.08;
    } else if (selectedNode && !isSelected && !isConnected) {
      ctx.globalAlpha = 0.15;
    }

    ctx.beginPath();
    ctx.arc(node.x, node.y, node.radius, 0, 2 * Math.PI);
    ctx.fillStyle = node.color;
    ctx.fill();

    if (isImpactSource) {
      ctx.strokeStyle = '#111827';
      ctx.lineWidth = 3.5;
      ctx.stroke();
    } else if (inImpact) {
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2.5;
      ctx.stroke();
    } else if (isSelected || isHovered) {
      ctx.strokeStyle = '#111827';
      ctx.lineWidth = 3;
      ctx.stroke();
    } else {
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    if (showLabels || (inImpact && transform.k > 0.2)) {
      ctx.font = (isSelected || isHovered) ? 'bold 11px Inter, system-ui, sans-serif' : '10px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#111827';
      const label = node.name.length > 22 ? node.name.substring(0, 20) + '…' : node.name;
      const pad = node.radius + 4;
      const dir = node.labelDir || 'bottom';

      if (dir === 'left') { ctx.textAlign = 'right'; ctx.textBaseline = 'middle'; ctx.fillText(label, node.x - pad, node.y); }
      else if (dir === 'right') { ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(label, node.x + pad, node.y); }
      else if (dir === 'top') { ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.fillText(label, node.x, node.y - pad); }
      else { ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillText(label, node.x, node.y + pad); }
    }

    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

function precomputeLabelDirs(nodes: SimNode[]) {
  const cellSize = 100;
  const grid = new Map<string, number[]>();
  for (let i = 0; i < nodes.length; i++) {
    const gx = Math.floor(nodes[i].x / cellSize);
    const gy = Math.floor(nodes[i].y / cellSize);
    const key = `${gx},${gy}`;
    const arr = grid.get(key);
    if (arr) arr.push(i); else grid.set(key, [i]);
  }
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const gx = Math.floor(node.x / cellSize);
    const gy = Math.floor(node.y / cellSize);
    let avgDx = 0, avgDy = 0, count = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const cell = grid.get(`${gx + dx},${gy + dy}`);
        if (!cell) continue;
        for (const j of cell) {
          if (j === i) continue;
          const ox = nodes[j].x - node.x;
          const oy = nodes[j].y - node.y;
          const d2 = ox * ox + oy * oy;
          if (d2 < 15000) { avgDx += ox; avgDy += oy; count++; }
        }
      }
    }
    if (count === 0) { node.labelDir = 'bottom'; }
    else if (Math.abs(avgDx) > Math.abs(avgDy)) { node.labelDir = avgDx > 0 ? 'left' : 'right'; }
    else { node.labelDir = avgDy > 0 ? 'top' : 'bottom'; }
  }
}

let _hitGrid: Map<string, SimNode[]> = new Map();
const _hitCellSize = 60;

function buildHitGrid(nodes: SimNode[]) {
  _hitGrid = new Map();
  for (const n of nodes) {
    const r = n.radius + 4;
    const x0 = Math.floor((n.x - r) / _hitCellSize);
    const x1 = Math.floor((n.x + r) / _hitCellSize);
    const y0 = Math.floor((n.y - r) / _hitCellSize);
    const y1 = Math.floor((n.y + r) / _hitCellSize);
    for (let gx = x0; gx <= x1; gx++) {
      for (let gy = y0; gy <= y1; gy++) {
        const key = `${gx},${gy}`;
        const arr = _hitGrid.get(key);
        if (arr) arr.push(n); else _hitGrid.set(key, [n]);
      }
    }
  }
}

function hitTestGrid(x: number, y: number): SimNode | null {
  const gx = Math.floor(x / _hitCellSize);
  const gy = Math.floor(y / _hitCellSize);
  const cell = _hitGrid.get(`${gx},${gy}`);
  if (!cell) return null;
  for (let i = cell.length - 1; i >= 0; i--) {
    const n = cell[i];
    const dx = n.x - x;
    const dy = n.y - y;
    if (dx * dx + dy * dy < (n.radius + 4) * (n.radius + 4)) return n;
  }
  return null;
}

// ── Legend ───────────────────────────────────────────────────────────────

function Legend({ counts }: { counts: Map<string, number> }) {
  return (
    <div style={{
      position: 'absolute', top: 12, right: 12, zIndex: 10,
      background: 'rgba(255,255,255,0.95)', borderRadius: 10,
      padding: '10px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
      maxHeight: 'calc(100% - 24px)', overflowY: 'auto', fontSize: 12,
    }}>
      {Array.from(counts.entries()).map(([cat, count]) => (
        <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 4px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: getColor(cat), flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: '#374151', minWidth: 22 }}>{count}</span>
          <span style={{ color: '#6b7280', textTransform: 'capitalize' }}>{cat}</span>
        </div>
      ))}
    </div>
  );
}

// ── Props ────────────────────────────────────────────────────────────────

interface TsGraphVisualizerProps {
  graphData: TsGraphData;
  onNodeSelect?: (node: TsNode | null) => void;
  selectedNodeId?: string | null;
  filterCategories?: string[];
  searchText?: string;
  showUnusedOnly?: boolean;
  showCyclesOnly?: boolean;
  circularNodeIds?: Set<string>;
  /** Node id → BFS depth from the impact source (0). Highlights the ripple. */
  impactMap?: Map<string, number> | null;
}

export const TsGraphVisualizer: React.FC<TsGraphVisualizerProps> = ({
  graphData,
  onNodeSelect,
  selectedNodeId,
  filterCategories = [],
  searchText = '',
  showUnusedOnly = false,
  showCyclesOnly = false,
  circularNodeIds = new Set(),
  impactMap = null,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simNodesRef = useRef<SimNode[]>([]);
  const simEdgesRef = useRef<SimEdge[]>([]);
  const nodeMapRef = useRef<Map<string, SimNode>>(new Map());
  const animFrameRef = useRef<number>(0);
  const workerRef = useRef<Worker | null>(null);

  const [layouting, setLayouting] = useState(true);
  const [selectedNode, setSelectedNode] = useState<SimNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<SimNode | null>(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const [interactionMode, setInteractionMode] = useState<'select' | 'pan'>('select');

  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const draggedNodeRef = useRef<SimNode | null>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);

  // Default view = the full graph: every file node and every export-symbol node,
  // with import (file→file) and uses (file→export) edges. Category filters let
  // the user collapse to a file-level view by hiding "Export". The special
  // "Unused" / "Cycles" toggles narrow to those subsets.
  const filteredData = useMemo(() => {
    let nodes: TsNode[];
    let edges: TsEdge[] = graphData.edges;

    if (showUnusedOnly) {
      const unusedIds = new Set(graphData.metadata.unusedExports.map(u => `${u.file}:${u.export}`));
      nodes = graphData.nodes.filter(n => unusedIds.has(n.id));
    } else if (showCyclesOnly) {
      nodes = graphData.nodes.filter(n => n.category !== 'export' && circularNodeIds.has(n.id));
    } else {
      nodes = graphData.nodes;
    }

    if (filterCategories.length > 0) {
      nodes = nodes.filter(n => filterCategories.includes(n.category));
    }
    if (searchText) {
      const lc = searchText.toLowerCase();
      nodes = nodes.filter(n => n.name.toLowerCase().includes(lc) || n.id.toLowerCase().includes(lc));
    }

    // Keep every edge whose both endpoints are visible nodes. (Some `uses`
    // edges point to files outside the scanned set and are dropped here.)
    const ids = new Set(nodes.map(n => n.id));
    edges = edges.filter(e => ids.has(e.source) && ids.has(e.target));

    return { nodes, edges };
  }, [graphData, filterCategories, searchText, showUnusedOnly, showCyclesOnly, circularNodeIds]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const n of filteredData.nodes) counts.set(n.category, (counts.get(n.category) || 0) + 1);
    return counts;
  }, [filteredData.nodes]);

  // ── Build sim data and run layout ──
  useEffect(() => {
    const w = containerRef.current?.clientWidth || 900;
    const h = containerRef.current?.clientHeight || 600;

    const simNodes: SimNode[] = filteredData.nodes.map(n => ({
      id: n.id,
      x: 0, y: 0, vx: 0, vy: 0,
      name: n.name,
      nameLen: n.name.length,
      category: n.category,
      color: getColor(n.category),
      radius: n.category === 'export' ? 7 : Math.max(8, Math.min(18, 7 + Math.sqrt(n.dependents || 0) * 2.5)),
      dependents: n.dependents,
    }));

    const simEdges: SimEdge[] = filteredData.edges.map(e => ({ source: e.source, target: e.target, type: e.type }));

    simNodesRef.current = simNodes;
    simEdgesRef.current = simEdges;
    const map = new Map<string, SimNode>();
    for (const n of simNodes) map.set(n.id, n);
    nodeMapRef.current = map;

    setLayouting(true);
    if (workerRef.current) { workerRef.current.terminate(); workerRef.current = null; }
    if (simNodes.length === 0) { setLayouting(false); return; }

    const worker = new Worker(getWorkerUrl());
    workerRef.current = worker;

    const nodeCount = simNodes.length;
    const iterations = nodeCount > 300 ? 150 : nodeCount > 100 ? 200 : 250;

    worker.onmessage = (evt) => {
      const positions: Array<{ id: string; x: number; y: number }> = evt.data.positions;
      for (const pos of positions) {
        const node = nodeMapRef.current.get(pos.id);
        if (node) { node.x = pos.x; node.y = pos.y; }
      }
      precomputeLabelDirs(simNodesRef.current);
      buildHitGrid(simNodesRef.current);
      setLayouting(false);
    };
    worker.postMessage({
      nodes: simNodes.map(n => ({ id: n.id, radius: n.radius, nameLen: n.nameLen })),
      edges: simEdges,
      width: w,
      height: h,
      iterations,
    });

    return () => { worker.terminate(); };
  }, [filteredData]);

  // Sync externally-selected node (from detail panel / node list)
  useEffect(() => {
    if (selectedNodeId) {
      const n = nodeMapRef.current.get(selectedNodeId);
      if (n) setSelectedNode(n);
    } else {
      setSelectedNode(null);
    }
  }, [selectedNodeId, filteredData]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    drawGraph(ctx, simNodesRef.current, simEdgesRef.current, nodeMapRef.current,
      transformRef.current, hoveredNode, selectedNode, w, h, impactMap ?? null);
  }, [hoveredNode, selectedNode, impactMap]);

  const scheduleRedraw = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(render);
  }, [render]);

  useEffect(() => { scheduleRedraw(); }, [scheduleRedraw]);
  useEffect(() => { scheduleRedraw(); }, [transform, scheduleRedraw]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => scheduleRedraw());
    ro.observe(container);
    return () => ro.disconnect();
  }, [scheduleRedraw]);

  const hitTest = useCallback((clientX: number, clientY: number): SimNode | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const t = transformRef.current;
    const x = (clientX - rect.left - t.x) / t.k;
    const y = (clientY - rect.top - t.y) / t.k;
    return hitTestGrid(x, y);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const node = hitTest(e.clientX, e.clientY);
    if (node) {
      draggedNodeRef.current = node;
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      dragMoved.current = false;
    } else if (interactionMode === 'pan') {
      isPanning.current = true;
      const t = transformRef.current;
      panStart.current = { x: e.clientX - t.x, y: e.clientY - t.y };
    } else {
      setSelectedNode(null);
      onNodeSelect?.(null);
    }
  }, [hitTest, interactionMode, onNodeSelect]);

  const lastHoverTime = useRef(0);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (draggedNodeRef.current) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const t = transformRef.current;
      draggedNodeRef.current.x = (e.clientX - rect.left - t.x) / t.k;
      draggedNodeRef.current.y = (e.clientY - rect.top - t.y) / t.k;
      const totalMove = Math.abs(e.clientX - dragStartPos.current.x) + Math.abs(e.clientY - dragStartPos.current.y);
      if (totalMove > 4) dragMoved.current = true;
      scheduleRedraw();
      canvas.style.cursor = 'move';
    } else if (isPanning.current) {
      transformRef.current = { ...transformRef.current, x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y };
      scheduleRedraw();
      if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
    } else {
      const now = performance.now();
      if (now - lastHoverTime.current < 32) return;
      lastHoverTime.current = now;
      const node = hitTest(e.clientX, e.clientY);
      if (node !== hoveredNode) setHoveredNode(node);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = node ? 'pointer' : (interactionMode === 'pan' ? 'grab' : 'default');
      }
    }
  }, [hitTest, scheduleRedraw, hoveredNode, interactionMode]);

  const handleMouseUp = useCallback(() => {
    if (draggedNodeRef.current) {
      if (!dragMoved.current) {
        const node = draggedNodeRef.current;
        setSelectedNode(node);
        const raw = filteredData.nodes.find(n => n.id === node.id);
        if (raw) onNodeSelect?.(raw);
      }
      draggedNodeRef.current = null;
    }
    if (isPanning.current) setTransform({ ...transformRef.current });
    isPanning.current = false;
    if (canvasRef.current) canvasRef.current.style.cursor = interactionMode === 'pan' ? 'grab' : 'default';
  }, [interactionMode, filteredData.nodes, onNodeSelect]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const zoom = e.deltaY < 0 ? 1.1 : 0.9;
      setTransform((t) => {
        const newK = Math.max(0.05, Math.min(5, t.k * zoom));
        return { k: newK, x: mx - (mx - t.x) * (newK / t.k), y: my - (my - t.y) * (newK / t.k) };
      });
    };
    canvas.addEventListener('wheel', handler, { passive: false });
    return () => canvas.removeEventListener('wheel', handler);
  }, []);

  // Percentile-trimmed bounds: edge-less nodes (e.g. unused exports) get pushed
  // far out by repulsion with nothing pulling them back, exploding the naive
  // bounding box so a fit made the connected core a tiny dot. Trimming the
  // extreme 3% per axis fits the graph body instead of the outliers. Also
  // returns the median point as a "dense core" centre for the readable view.
  const getTrimmedBounds = useCallback(() => {
    const nodes = simNodesRef.current;
    if (nodes.length === 0) return null;
    const xs = nodes.map((n) => n.x).sort((a, b) => a - b);
    const ys = nodes.map((n) => n.y).sort((a, b) => a - b);
    const t = Math.min(Math.floor(nodes.length * 0.03), Math.max(nodes.length - 2, 0));
    return {
      minX: xs[t], maxX: xs[xs.length - 1 - t],
      minY: ys[t], maxY: ys[ys.length - 1 - t],
      medX: xs[Math.floor(xs.length / 2)],
      medY: ys[Math.floor(ys.length / 2)],
    };
  }, []);

  const fitToView = useCallback(() => {
    const canvas = canvasRef.current;
    const b = getTrimmedBounds();
    if (!canvas || !b) return;
    const pad = 50;
    const w = canvas.clientWidth - pad * 2;
    const h = canvas.clientHeight - pad * 2;
    const k = Math.min(Math.max(Math.min(w / (b.maxX - b.minX || 1), h / (b.maxY - b.minY || 1)), 0.05), 2);
    const cx = (b.minX + b.maxX) / 2;
    const cy = (b.minY + b.maxY) / 2;
    setTransform({ k, x: canvas.clientWidth / 2 - cx * k, y: canvas.clientHeight / 2 - cy * k });
  }, [getTrimmedBounds]);

  // Readable view: open zoomed to where node labels render (k > 0.35), centred
  // on the dense core, so the user starts from a legible graph and zooms/pans
  // out for the overview instead of starting from a dot. Small graphs that
  // already fit at a readable zoom just use the fit.
  const readableView = useCallback(() => {
    const canvas = canvasRef.current;
    const b = getTrimmedBounds();
    if (!canvas || !b) return;
    const pad = 50;
    const w = canvas.clientWidth - pad * 2;
    const h = canvas.clientHeight - pad * 2;
    const fitK = Math.min(w / (b.maxX - b.minX || 1), h / (b.maxY - b.minY || 1));
    const k = Math.min(Math.max(fitK, 0.55), 2);
    // Zoomed past the fit → centre on the dense core; otherwise bbox centre.
    const cx = k > fitK ? b.medX : (b.minX + b.maxX) / 2;
    const cy = k > fitK ? b.medY : (b.minY + b.maxY) / 2;
    setTransform({ k, x: canvas.clientWidth / 2 - cx * k, y: canvas.clientHeight / 2 - cy * k });
  }, [getTrimmedBounds]);

  const zoomBy = useCallback((factor: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cx = canvas.clientWidth / 2;
    const cy = canvas.clientHeight / 2;
    setTransform((t) => {
      const newK = Math.max(0.05, Math.min(5, t.k * factor));
      return { k: newK, x: cx - (cx - t.x) * (newK / t.k), y: cy - (cy - t.y) * (newK / t.k) };
    });
  }, []);

  // Clear any selection and return to the readable start view.
  const resetView = useCallback(() => {
    setSelectedNode(null);
    onNodeSelect?.(null);
    readableView();
  }, [onNodeSelect, readableView]);

  // Open at the readable view once layout finishes (and after every re-layout
  // from a filter/search change) — legible labels, centred on the dense core,
  // instead of a whole-graph fit that shrinks large graphs to a dot.
  useEffect(() => {
    if (!layouting && simNodesRef.current.length > 0) {
      // Defer one frame so the canvas has its final size before we measure.
      const id = requestAnimationFrame(() => readableView());
      return () => cancelAnimationFrame(id);
    }
  }, [layouting, readableView]);

  const totalNodes = simNodesRef.current.length;
  const totalEdges = simEdgesRef.current.length;

  return (
    <div ref={containerRef} className="relative w-full h-full bg-gray-50">
      {layouting && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
          <div className="text-sm text-gray-500">Laying out graph...</div>
        </div>
      )}
      {!layouting && totalNodes === 0 && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="text-sm text-gray-500">No nodes match the current filters</div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 15,
        display: 'flex', alignItems: 'center', padding: '8px 16px', gap: 8,
        background: 'rgba(255,255,255,0.95)', borderBottom: '1px solid #e5e7eb',
      }}>
        <div style={{ display: 'inline-flex', borderRadius: 6, border: '1px solid #d1d5db', overflow: 'hidden', fontSize: 12 }}>
          <button
            onClick={() => setInteractionMode('select')}
            title="Select mode: click nodes to inspect"
            style={{ padding: '4px 10px', border: 'none', cursor: 'pointer', background: interactionMode === 'select' ? '#4f46e5' : 'white', color: interactionMode === 'select' ? 'white' : '#374151', fontWeight: 500 }}
          >Select</button>
          <button
            onClick={() => setInteractionMode('pan')}
            title="Pan mode: drag canvas to move around"
            style={{ padding: '4px 10px', border: 'none', cursor: 'pointer', borderLeft: '1px solid #d1d5db', background: interactionMode === 'pan' ? '#4f46e5' : 'white', color: interactionMode === 'pan' ? 'white' : '#374151', fontWeight: 500 }}
          >Pan</button>
        </div>
        <button onClick={() => zoomBy(1.3)} title="Zoom in" style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 600 }}>+</button>
        <button onClick={() => zoomBy(1 / 1.3)} title="Zoom out" style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 600 }}>&minus;</button>
        <button onClick={fitToView} title="Fit graph to viewport" style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#374151' }}>Fit</button>
        <button
          onClick={resetView}
          title="Clear selection and re-center the whole graph"
          style={{
            background: selectedNode ? '#4f46e5' : 'none',
            border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px',
            fontSize: 12, cursor: 'pointer', color: selectedNode ? 'white' : '#374151', fontWeight: 500,
          }}
        >Reset</button>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{totalNodes} nodes &middot; {totalEdges} edges</div>
      </div>

      <Legend counts={categoryCounts} />

      {hoveredNode && !selectedNode && (
        <div style={{
          position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.8)', color: 'white', padding: '6px 12px',
          borderRadius: 8, fontSize: 12, zIndex: 10, pointerEvents: 'none',
        }}>
          <strong>{hoveredNode.name}</strong>
          {' · '}{hoveredNode.category}
          {hoveredNode.category !== 'export' && ` · ${hoveredNode.dependents} dependents`}
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', top: 40, left: 0, right: 0, bottom: 0, width: '100%', height: 'calc(100% - 40px)', cursor: 'grab' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </div>
  );
};
